import { useTranslation } from '@starsoft/common/hooks';
import styles from './styles.module.scss';
import { useCreateKycFaceRecognition } from '@starsoft/common/api/kyc/mutations/useCreateKycFaceRecognition';
import { KYCUserIdentityFormContentDocumentSubmissionProps } from './props';
import { Button } from '@starsoft/common/components/Button';
import { useEffect, useState } from 'react';
import { useUserOriginId } from '@starsoft/common/hooks/user-identity/useUserOriginId';

/**
 * KYC User Identity Form Content Document Submission Component
 * A component that handles document submission for KYC user identity verification
 * by providing a button to initiate face recognition
 *
 * @component
 * @param {Object} props - Component props
 * @param {string|number} props.originId - The origin ID for the KYC verification
 * @returns {JSX.Element} Document submission form with submit button
 *
 * @example
 * // Basic usage
 * <KYCUserIdentityFormContentDocumentSubmission
 *   originId="12345"
 * />
 */
export default function KYCUserIdentityFormContentDocumentSubmission({
  originId,
}: KYCUserIdentityFormContentDocumentSubmissionProps) {
  const { t } = useTranslation('common');
  const { createKycFaceRecognitionAsync, isPending } =
    useCreateKycFaceRecognition();
  const [callbackUrl, setCallbackUrl] = useState<string | null>(null);
  const userOriginId = useUserOriginId();

  function handleGenerateFaceRecognitionLink() {
    const createUrl = async () => {
      const { url } = await createKycFaceRecognitionAsync({
        originId: userOriginId ?? originId?.toString(),
      });
      setCallbackUrl(url);
    };

    createUrl();
  }

  useEffect(handleGenerateFaceRecognitionLink, []);

  /**
   * Handles the creation of KYC face recognition request
   * Calls the face recognition API with the origin ID
   */
  function handleCreateKycFaceRecognition() {
    window.open(callbackUrl, '_blank');
  }

  return (
    <div className={styles.container}>
      <Button onClick={handleCreateKycFaceRecognition} isloading={isPending}>
        {t('document_submission_button')}
      </Button>
    </div>
  );
}
